import { PolicyBlock } from '@/types/policy';

const policyHeader: readonly PolicyBlock[] = [
  { kind: 'brand', text: 'Shieldix - VPN & AI Protection' },
  { kind: 'effectiveDate', text: 'Effective Date: August 12, 2026' },
  { kind: 'metadata', text: 'Company: Alfamedia MF Ltd' },
  { kind: 'metadata', text: 'Registration Number: 207572956 | VAT: BG207572956' },
  {
    kind: 'metadata',
    text: 'Address: Bulgaria, Plovdiv 4000, bul. Asenovgradsko shousse 2, ent. V, do 16, ap. 79',
  },
  {
    kind: 'metadata',
    text: 'Support: support@getshieldix.com | Legal & Privacy: legal@getshieldix.com',
  },
  { kind: 'metadata', text: 'Website: https://getshieldix.com' },
  { kind: 'spacer', text: '' },
];

export const privacyPolicy: readonly PolicyBlock[] = [
  ...policyHeader,
  { kind: 'heading', text: '1. Overview' },
  {
    kind: 'body',
    text: 'Alfamedia MF Ltd is the controller of personal data described in this Policy. This Policy explains what we process when you use Shieldix, our website, account dashboard, and support services.',
  },
  { kind: 'heading', text: '2. VPN No-Log Practices' },
  {
    kind: 'body',
    text: 'We do not retain logs of your browsing activity, DNS queries, traffic content, or a history of destinations visited through the VPN. Your originating IP address may be processed transiently to establish and route a VPN connection, but Shieldix does not retain it as a browsing-activity log.',
  },
  { kind: 'heading', text: '3. Data We Process' },
  { kind: 'subheading', text: 'Account and Support' },
  {
    kind: 'body',
    text: 'Email address, account/internal identifiers, subscription status, registration or login timestamps, and information you provide to support.',
  },
  { kind: 'subheading', text: 'Payments' },
  {
    kind: 'body',
    text: 'Payments are handled by external payment providers, including Stripe where shown at checkout. We do not directly store your full card number or security code. We may receive transaction ID, amount, currency, payment status, subscription status, refund/dispute status, and limited payment-method metadata needed to manage billing.',
  },
  { kind: 'body', text: 'Stripe privacy information: https://stripe.com/privacy' },
  { kind: 'subheading', text: 'Analytics and Attribution' },
  {
    kind: 'body',
    text: 'We use AppsFlyer for attribution, analytics, campaign measurement, and fraud prevention. Depending on your device settings, consent, platform rules, and our configuration, AppsFlyer may process device/OS information, IP address, app or device identifiers, install/session data, configured in-app events, and campaign or attribution data.',
  },
  {
    kind: 'body',
    text: 'AppsFlyer privacy information: https://www.appsflyer.com/legal/services-privacy-policy/',
  },
  { kind: 'subheading', text: 'Diagnostics and Device Features' },
  {
    kind: 'body',
    text: 'We may process limited technical diagnostics such as app version, OS information, connection success/failure, timestamps, crash data, and error codes. Device permissions are requested only for features you choose to use. Content processed entirely on your device is not sent to Shieldix servers.',
  },
  { kind: 'heading', text: '4. Why We Process Data' },
  {
    kind: 'body',
    text: 'We process data to provide and secure the Service, manage accounts and subscriptions, process payments, provide support, measure attribution and performance, prevent fraud or abuse, troubleshoot technical issues, comply with legal/accounting/payment obligations, and establish or defend legal claims.',
  },
  {
    kind: 'body',
    text: 'Where GDPR or similar law applies, the legal basis may be performance of a contract, legal obligation, legitimate interests, or consent where consent is required.',
  },
  { kind: 'heading', text: '5. Sharing' },
  {
    kind: 'body',
    text: 'We do not sell personal data for monetary consideration. We disclose data only as needed to service providers such as payment processors (including Stripe), attribution/analytics providers (including AppsFlyer), hosting/security/support providers, professional advisers, and authorities where legally required. Providers acting for us are subject to applicable contractual and legal safeguards.',
  },
  { kind: 'heading', text: '6. International Data Transfers' },
  {
    kind: 'body',
    text: 'Stripe, AppsFlyer, or other service providers may process personal data outside the EEA. Where this occurs, transfers are handled using safeguards required by applicable law, such as an adequacy decision or Standard Contractual Clauses. Details are available on request.',
  },
  { kind: 'heading', text: '7. Retention and Security' },
  {
    kind: 'body',
    text: 'We keep personal data only as long as needed for the purposes above and applicable legal, tax, accounting, security, fraud, and dispute requirements. Temporary VPN-related diagnostics, if generated, are generally deleted within 96 hours unless a longer period is reasonably needed to investigate a security, fraud, abuse, or legal issue. We use reasonable technical and organizational security measures, but no system is completely secure.',
  },
  { kind: 'heading', text: '8. Your Rights' },
  {
    kind: 'body',
    text: 'Depending on applicable law, you may have rights to access, correct, delete, restrict or object to processing, receive portable data, withdraw consent, opt out of certain tracking or advertising uses, and complain to a competent data-protection authority. Contact legal@getshieldix.com. Where GDPR applies, we respond without undue delay and generally within one month, subject to lawful extensions.',
  },
  { kind: 'heading', text: '9. Changes and Contact' },
  {
    kind: 'body',
    text: 'We may update this Policy from time to time and will post the updated version with a new effective date. Privacy: legal@getshieldix.com | Support: support@getshieldix.com | Website: https://getshieldix.com',
  },
];

export const subscriptionRefundPolicy: readonly PolicyBlock[] = [
  ...policyHeader,
  { kind: 'heading', text: '1. Subscription Offers' },
  {
    kind: 'body',
    text: 'The exact offer shown and accepted at checkout controls your purchase. Current offers may include:',
  },
  { kind: 'bullet', text: '$0.99 for the first 3 days, then $14.99 every 7 days until canceled;' },
  { kind: 'bullet', text: '$14.99 per week; or' },
  { kind: 'bullet', text: '$34.99 per month.' },
  {
    kind: 'body',
    text: 'Paid plans provide the premium features included in the offer shown at checkout.',
  },
  { kind: 'heading', text: '2. Automatic Renewal and Authorization' },
  {
    kind: 'body',
    text: 'Subscriptions renew automatically until canceled. By completing checkout, you authorize Alfamedia MF Ltd and its payment provider to charge your payment method for the initial amount and each recurring renewal at the price and frequency disclosed at checkout. For the $0.99 offer, $0.99 is charged when you subscribe and, unless canceled, $14.99 is charged every 7 days beginning after the 3-day introductory period.',
  },
  { kind: 'heading', text: '3. Confirmation and Cancellation' },
  {
    kind: 'body',
    text: 'After enrollment, we provide an electronic confirmation with the material subscription terms and instructions for managing or canceling the subscription. You may cancel before your next billing date at https://getshieldix.com. Cancellation stops future renewals and does not automatically refund charges already processed.',
  },
  { kind: 'heading', text: '4. Refund Policy' },
  {
    kind: 'body',
    text: 'Except where required by applicable law or card-network rules, subscription payments are non-refundable. We do not provide automatic refunds for unused time, failure to use the Service, forgetting to cancel, change of mind, or cancellation after a charge has been processed.',
  },
  {
    kind: 'body',
    text: 'A refund, correction, price reduction, or other remedy will be provided where required by law or applicable card-network rules, including for a verified billing error, duplicate charge, or material failure by us to provide the paid Service as agreed.',
  },
  { kind: 'heading', text: '5. Billing Errors and Unrecognized Charges' },
  {
    kind: 'body',
    text: 'If you believe a charge is incorrect, duplicated, or unauthorized, contact support@getshieldix.com promptly with the subscription email, charge date, amount, and relevant transaction details. Do not email full card numbers or security codes. Nothing in this Policy limits rights available through your card issuer or payment provider.',
  },
  { kind: 'heading', text: '6. Technical Problems' },
  {
    kind: 'body',
    text: 'A technical problem does not automatically create a refund right. Contact support so we can investigate. Where a verified failure attributable to us gives you a refund or other remedy under applicable law or card-network rules, we will provide that remedy.',
  },
  { kind: 'heading', text: '7. EU/EEA Right of Withdrawal' },
  {
    kind: 'body',
    text: 'If EU/EEA consumer law gives you a statutory right to withdraw from a distance service contract, you generally have 14 days from conclusion of the contract to do so without giving a reason. If you expressly request immediate performance and then validly withdraw during that period, you may be charged a proportionate amount for service already supplied where permitted by law.',
  },
  {
    kind: 'body',
    text: 'To withdraw, email an unequivocal statement to support@getshieldix.com or legal@getshieldix.com before the deadline. This section does not limit any stronger mandatory rights that apply to you.',
  },
  { kind: 'heading', text: '8. Refund Method and Timing' },
  {
    kind: 'body',
    text: 'Approved or legally required refunds are normally returned to the original payment method. Where EU/EEA withdrawal law requires reimbursement, we will reimburse the required amount without undue delay and no later than the applicable statutory deadline, generally 14 days after notice of withdrawal. Bank or card-network posting times may vary.',
  },
  { kind: 'heading', text: '9. Model EU/EEA Withdrawal Form' },
  {
    kind: 'body',
    text: 'To: Alfamedia MF Ltd, Bulgaria, Plovdiv 4000, bul. Asenovgradsko shousse 2, ent. V, do 16, ap. 79 | support@getshieldix.com',
  },
  {
    kind: 'body',
    text: 'I hereby give notice that I withdraw from my contract for the Shieldix subscription/service.',
  },
  {
    kind: 'form',
    text: 'Name: ____________________\nSubscription email: ____________________\nDate ordered/subscribed: ____________________\nDate: ____________________',
  },
  { kind: 'heading', text: '10. Contact and Changes' },
  {
    kind: 'body',
    text: 'Billing/support: support@getshieldix.com | Subscription management: https://getshieldix.com. We may update this Policy prospectively. Updates do not reduce mandatory rights that applied when a transaction was completed.',
  },
];

export const termsOfService: readonly PolicyBlock[] = [
  ...policyHeader,
  { kind: 'heading', text: '1. Acceptance' },
  {
    kind: 'body',
    text: 'By using Shieldix or purchasing a subscription, you agree to these Terms. If you do not agree, do not use the Service.',
  },
  { kind: 'heading', text: '2. Service and License' },
  {
    kind: 'body',
    text: 'Shieldix provides VPN, security, privacy, cleaning, and related features as made available in the app or website. We grant you a limited, personal, non-exclusive, non-transferable, revocable license to use the Service for lawful purposes.',
  },
  { kind: 'heading', text: '3. Acceptable Use' },
  {
    kind: 'body',
    text: 'You may not misuse the Service, interfere with its operation, access systems without authorization, distribute malware or spam, infringe third-party rights, reverse-engineer the Service except where law permits, resell it without permission, or use it for unlawful activity. We may suspend or terminate access for material violations.',
  },
  { kind: 'heading', text: '4. Subscriptions and Billing' },
  {
    kind: 'body',
    text: 'Subscription prices, billing frequency, and any introductory offer are shown at checkout and control your purchase. Where displayed, Shieldix may offer $0.99 for the first 3 days, followed by automatic renewal at $14.99 every 7 days until canceled. Other plans, including $14.99/week or $34.99/month, may also be offered.',
  },
  {
    kind: 'body',
    text: 'By completing checkout, you authorize Alfamedia MF Ltd and its payment provider to charge the payment method you provide for the initial purchase and recurring renewals at the price and frequency disclosed at checkout.',
  },
  { kind: 'heading', text: '5. Cancellation' },
  {
    kind: 'body',
    text: 'You may cancel automatic renewal before your next billing date through the subscription-management tools at https://getshieldix.com. Cancellation stops future renewals but does not automatically reverse a charge already processed.',
  },
  { kind: 'heading', text: '6. Refunds and Consumer Rights' },
  {
    kind: 'body',
    text: 'Subscription payments are non-refundable except where required by applicable law, applicable card-network rules, or our Refund Policy, including verified billing errors or a material failure to provide the paid Service as agreed. Nothing in these Terms limits non-waivable consumer rights. See our Subscription Information & Refund Policy for details.',
  },
  { kind: 'heading', text: '7. Service Availability' },
  {
    kind: 'body',
    text: 'The Service is provided on an "as available" basis. We do not guarantee uninterrupted availability, a particular VPN speed, server location, compatibility, or error-free operation. We may update, modify, or discontinue features where permitted by law.',
  },
  { kind: 'heading', text: '8. Accounts, Security, and Termination' },
  {
    kind: 'body',
    text: 'You are responsible for maintaining the security of your account and for activity under it. We may restrict or terminate access where reasonably necessary for security, fraud prevention, legal compliance, non-payment, or a material breach of these Terms.',
  },
  { kind: 'heading', text: '9. Intellectual Property' },
  {
    kind: 'body',
    text: 'Shieldix software, branding, content, and related intellectual property are owned by Alfamedia MF Ltd or its licensors. No rights are granted except the limited license in these Terms.',
  },
  {
    kind: 'body',
    text: 'Apple and the Apple logo are trademarks of Apple Inc., registered in the U.S. and other countries. App Store is a service mark of Apple Inc.',
  },
  { kind: 'heading', text: '10. Disclaimers and Liability' },
  {
    kind: 'body',
    text: 'To the maximum extent permitted by law, Alfamedia MF Ltd is not liable for indirect, incidental, special, or consequential losses, loss of data, lost profits, or losses caused by third-party networks or services. Any limitation applies only to the extent permitted by applicable law and does not exclude liability that cannot legally be excluded.',
  },
  { kind: 'heading', text: '11. Indemnity' },
  {
    kind: 'body',
    text: 'To the extent permitted by law, you agree to indemnify Alfamedia MF Ltd against third-party claims arising from your unlawful use of the Service, material breach of these Terms, or infringement of third-party rights.',
  },
  { kind: 'heading', text: '12. Privacy' },
  {
    kind: 'body',
    text: 'Our Privacy Policy explains how personal data is processed. It does not replace any consent or other legal basis required by applicable data-protection law.',
  },
  { kind: 'heading', text: '13. Changes' },
  {
    kind: 'body',
    text: 'We may update the Service or these Terms. Material changes will be notified where required by law. We will not use a Terms update alone to impose a materially different recurring price or billing obligation where separate notice or consent is legally required.',
  },
  { kind: 'heading', text: '14. Governing Law and Disputes' },
  {
    kind: 'body',
    text: 'These Terms are governed by Bulgarian law, without prejudice to mandatory consumer protections in your country of residence. Bulgarian courts have jurisdiction unless applicable law gives you the right to bring or defend a claim elsewhere.',
  },
  { kind: 'heading', text: '15. Contact' },
  {
    kind: 'body',
    text: 'Support: support@getshieldix.com | Legal: legal@getshieldix.com | Website: https://getshieldix.com',
  },
];
